import { z } from 'zod';
import { IdSchema, TimestampSchema } from './common.js';
import {
  DestinationSchema, InboundRouteSaveSchema, IvrMenuUpsertSchema, QueueUpsertSchema, RingGroupUpsertSchema,
  ScheduleRuleSchema, ScheduleUpsertSchema,
} from './tms.js';

/**
 * Call flows (docs/41): Classic and Flows, two ways of editing the one set of routing.
 *
 * The mode is the account's own choice, made in the client portal. It changes which editor opens,
 * never what a call does: both edit the same pieces, and the switch runs those.
 */
export const RoutingModeSchema = z.enum(['classic', 'flows']);
export type RoutingMode = z.infer<typeof RoutingModeSchema>;

export const RoutingModeSaveSchema = z.object({ mode: RoutingModeSchema }).strict();

// ------------------------------------------------------------------ message
/** A recording, then on to the next step (docs/41 §3). */
export const FlowMessageSchema = z.object({
  id: IdSchema,
  tenant_id: IdSchema,
  name: z.string(),
  media_id: IdSchema.nullable(),
  media_name: z.string().nullable(),
  next_destination: DestinationSchema.nullable(),
  created_at: TimestampSchema,
});
export type FlowMessage = z.infer<typeof FlowMessageSchema>;

export const FlowMessageUpsertSchema = z.object({
  name: z.string().trim().min(1).max(120),
  media_id: IdSchema,
  /** Where the caller goes once it has played. None ends the call. */
  next_destination: DestinationSchema.nullable().optional(),
}).strict();
export type FlowMessageUpsert = z.infer<typeof FlowMessageUpsertSchema>;

// -------------------------------------------------------------- hours check
/** Open or closed, anywhere in a flow (docs/41 §3): a shared schedule, or hours of its own. */
export const HoursCheckHoursSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('schedule'), schedule_id: IdSchema }),
  z.object({
    mode: z.literal('own'),
    timezone: z.string().max(64).nullable().optional(),
    rules: ScheduleUpsertSchema.shape.rules,
  }),
]);

export const HoursCheckSchema = z.object({
  id: IdSchema,
  tenant_id: IdSchema,
  name: z.string(),
  hours: z.object({
    mode: z.enum(['schedule', 'own']),
    schedule_id: IdSchema.nullable(),
    /** The shared schedule's name; null for hours of its own. */
    schedule_name: z.string().nullable(),
    timezone: z.string(),
    /** Its own hours; empty for a shared schedule. */
    rules: z.array(ScheduleRuleSchema),
    /** From the evaluation the switch makes at call time. */
    open_now: z.boolean().nullable(),
    explanation: z.string().nullable(),
  }),
  open_destination: DestinationSchema.nullable(),
  closed_destination: DestinationSchema.nullable(),
  created_at: TimestampSchema,
});
export type HoursCheck = z.infer<typeof HoursCheckSchema>;

export const HoursCheckUpsertSchema = z.object({
  name: z.string().trim().min(1).max(120),
  hours: HoursCheckHoursSchema,
  /** None ends the call on that branch. */
  open_destination: DestinationSchema.nullable().optional(),
  closed_destination: DestinationSchema.nullable().optional(),
}).strict();
export type HoursCheckUpsert = z.infer<typeof HoursCheckUpsertSchema>;

// -------------------------------------------------------------------- drafts
/**
 * The pieces a flow is made of, as a draft names them. Each saves through the same schema, and the
 * same code, as its own page in Classic.
 */
export const FlowPieceKindSchema = z.enum(['inbound_route', 'ivr', 'ring_group', 'queue', 'message', 'hours']);
export type FlowPieceKind = z.infer<typeof FlowPieceKindSchema>;

export const FLOW_PIECE_SCHEMAS = {
  inbound_route: InboundRouteSaveSchema,
  ivr: IvrMenuUpsertSchema,
  ring_group: RingGroupUpsertSchema,
  queue: QueueUpsertSchema,
  message: FlowMessageUpsertSchema,
  hours: HoursCheckUpsertSchema,
} as const;

/**
 * One piece as the draft would have it. `body` is checked against the piece's own schema when the
 * draft is published, not before: a draft may hold a step nobody has finished yet. A new piece's id
 * is made by the portal, so steps in the draft can point at it before it exists.
 */
export const FlowChangeSchema = z.object({
  kind: FlowPieceKindSchema,
  id: IdSchema,
  op: z.enum(['create', 'update']),
  body: z.record(z.unknown()),
  /** When the draft first changed this piece; set by the server. A newer piece is a conflict. */
  base: z.string().nullable().optional(),
}).strict();
export type FlowChange = z.infer<typeof FlowChangeSchema>;

export const FlowDraftSaveSchema = z.object({
  /** The route the flow starts from; null for a flow whose route is new in the draft. */
  route_id: IdSchema.nullable(),
  changes: z.array(FlowChangeSchema).max(200),
}).strict();
export type FlowDraftSave = z.infer<typeof FlowDraftSaveSchema>;

export const FlowDraftSchema = z.object({
  id: IdSchema,
  route_id: IdSchema.nullable(),
  /** The route's name as the draft has it, else as it is live. */
  name: z.string(),
  changes: z.array(FlowChangeSchema),
  updated_at: TimestampSchema,
  updated_by: z.string().nullable(),
});
export type FlowDraft = z.infer<typeof FlowDraftSchema>;

/** Why a draft could not be published, piece by piece. */
export const FlowProblemSchema = z.object({
  kind: FlowPieceKindSchema,
  id: IdSchema,
  name: z.string(),
  problem: z.enum(['invalid', 'changed', 'missing']),
  message: z.string(),
});
export type FlowProblem = z.infer<typeof FlowProblemSchema>;

export const FlowPublishResultSchema = z.object({
  route_id: IdSchema,
  /** Pieces written. */
  applied: z.number().int(),
  /** New pieces the flow no longer reaches, left out. */
  dropped: z.number().int(),
});
export type FlowPublishResult = z.infer<typeof FlowPublishResultSchema>;

// ------------------------------------------------------------------ AI: describe it in a sentence
/**
 * A flow described in words (docs/41 §7). What comes back is a proposal for the draft: where the
 * route's calls should go first, and the new steps that leads through. The editor puts it into the
 * draft; nothing reaches a call until somebody publishes.
 */
export const FlowAiRequestSchema = z.object({
  prompt: z.string().trim().min(3).max(1000),
  /** The flow's name, so the model can name its steps after it. */
  route_name: z.string().max(80).optional(),
}).strict();
export type FlowAiRequest = z.infer<typeof FlowAiRequestSchema>;

export const FlowAiResultSchema = z.object({
  /** Where the route's calls go first; null when nothing in the description could be matched. */
  start: DestinationSchema.nullable(),
  /** New steps only: everything already in the account is referred to by its id. */
  changes: z.array(FlowChangeSchema),
  /** What the model could not match or assumed, in words for the person who asked. */
  notes: z.array(z.string()),
  /** Whose key paid for it. */
  key: z.enum(['account', 'platform']),
});
export type FlowAiResult = z.infer<typeof FlowAiResultSchema>;
